import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { COMPANY_DETAILS_TABS, COMPANY_EDIT_STEPS } from 'src/services/consts/company.const'
import { ROUTES } from 'src/services/consts/routes.const'

// Breadcrumb types
export const BREADCRUMB = {
  COMPANIES: 'COMPANIES',
  COMPANY: 'COMPANY',
  EDIT: 'EDIT',
  PRODUCT: 'PRODUCT',
  SUBPROCESSOR: 'SUBPROCESSOR',
}

// Global breadcrumbs state
const breadcrumbs = ref([])

export const useBreadcrumbs = () => {
  const router = useRouter()
  const route = useRoute()

  const setBreadcrumbs = (type, data = {}) => {
    const companyId = route.params.id
    const isEditPage = route.name === ROUTES.COMPANY_EDIT
    const hasProductId = route.query.productId
    const hasSubprocessorId = route.query.subprocessorId
    const hasEntityId = hasProductId || hasSubprocessorId

    const tempBreadcrumbs = []

    // Always start with Companies if we have a company context
    if (companyId) {
      tempBreadcrumbs.push({
        label: 'Companies',
        to: { name: ROUTES.COMPANY },
      })

      // Add company breadcrumb
      const to =
        (isEditPage || hasEntityId) && companyId
          ? { name: ROUTES.COMPANY_INFO, params: { id: companyId, tab: COMPANY_DETAILS_TABS.INFO } }
          : null

      tempBreadcrumbs.push({
        label: data.companyName || 'Company',
        to,
        loading: hasEntityId ? false : data.loading,
      })
    }

    // Add Edit breadcrumb if on edit page
    if (isEditPage) {
      let to = null
      if (hasProductId)
        to = { name: ROUTES.COMPANY_EDIT, params: { id: companyId, step: COMPANY_EDIT_STEPS.INFO } }
      else if (hasSubprocessorId)
        to = {
          name: ROUTES.COMPANY_EDIT,
          params: { id: companyId, step: COMPANY_EDIT_STEPS.SUBPROCESSORS },
        }
      tempBreadcrumbs.push({
        label: 'Edit',
        to,
      })
    }

    // Add Product breadcrumb if viewing/editing a product
    if (hasProductId) {
      tempBreadcrumbs.push({
        label: formatEntityBreadcrumbLabel('Product', data.productName, 'Product'),
        to: null,
        loading: data.loading,
      })
    }

    // Add Subprocessor breadcrumb if viewing/editing a subprocessor
    if (hasSubprocessorId) {
      tempBreadcrumbs.push({
        label: formatEntityBreadcrumbLabel('Subprocessor', data.subprocessorName, 'Subprocessor'),
        to: null,
        loading: data.loading,
      })
    }

    breadcrumbs.value = tempBreadcrumbs
  }

  const clearBreadcrumbs = () => {
    breadcrumbs.value = []
  }

  const navigateTo = (to) => {
    if (to) {
      router.push(to)
    }
  }

  const setCompanyBreadcrumbs = ({ companyName, loading = false }) => {
    setBreadcrumbs(BREADCRUMB.COMPANY, { companyName, loading })
  }

  const setProductBreadcrumbs = ({ companyName, productName, loading = false }) => {
    setBreadcrumbs(BREADCRUMB.PRODUCT, { companyName, productName, loading })
  }

  const setSubprocessorBreadcrumbs = ({ companyName, subprocessorName, loading = false }) => {
    setBreadcrumbs(BREADCRUMB.SUBPROCESSOR, { companyName, subprocessorName, loading })
  }

  const removeProductBreadcrumbs = () => {
    breadcrumbs.value = breadcrumbs.value.slice(0, -1)
  }

  const removeSubprocessorBreadcrumbs = () => {
    breadcrumbs.value = breadcrumbs.value.slice(0, -1)
  }

  return {
    breadcrumbs,
    setBreadcrumbs,
    setCompanyBreadcrumbs,
    setProductBreadcrumbs,
    setSubprocessorBreadcrumbs,
    clearBreadcrumbs,
    removeProductBreadcrumbs,
    removeSubprocessorBreadcrumbs,
    navigateTo,
  }
}

function formatEntityBreadcrumbLabel(prefix, name, fallback) {
  const displayName = name?.trim() || fallback
  return `${prefix}: ${displayName}`
}
